// src/api/healthcare/ClinikoImportApi.ts
/**
 * @file The Cliniko → Xoe MIGRATION run API (S14, ClickUp 86d418ur4)
 * @module api/clinikoImportApi
 *
 * ═══ THIS IS NOT `ClinikoSyncApi` ════════════════════════════════════════════════════════════
 * `ClinikoSyncApi.ts` drives the ONGOING mirror of a Cliniko account the clinic is still working
 * in. These four functions drive the ONE-SHOT import that moves a clinic OFF Cliniko: a run is
 * queued, not performed, and every one of them returns in milliseconds while the work happens on
 * `ProcessClinikoImportRunJob` for up to six hours.
 *
 * ═══ ⚠️ `VITE_HEALTHCARE_SERVER` ALREADY ENDS IN `/api` ══════════════════════════════════════
 * Every ConfigMap sets it to `https://healthcare.<host>/api`, which is why `ClinikoSyncApi.ts`
 * writes `${API_BASE_URL}/practitioner/cliniko/...` and never `${API_BASE_URL}/api/practitioner/...`.
 * This module builds its URLs the same way. Adding the prefix would 404 in every environment —
 * and identically in dev, so nothing local would catch it.
 *
 * ═══ THE GATE IS `hasCredentials()` ONLY ═════════════════════════════════════════════════════
 * Deliberately not the ongoing-sync toggles: a clinic migrating away has every reason to have the
 * appointment mirror switched off and must still be able to pull its history across. So these
 * routes stay callable for an integration whose `sync_*` switches are all false.
 */

import { ENV } from "../../../config/runtimeEnv";

import {
	ClinikoImportRun,
	ClinikoImportRunListResponse,
	ClinikoImportRunStartResponse,
} from "../../../types/healthcare/ClinikoImportTypes";
import { http } from "../../../lib/http/client";

const API_BASE_URL = ENV.VITE_HEALTHCARE_SERVER;

/** This repo has NO axios interceptor, so every module attaches the bearer by hand. */

/**
 * Queue an import run. **202 + `run_uuid`** — the work has not started, only been accepted.
 *
 * ⚠️ **A 409 IS THE NORMAL SECOND ANSWER, NOT A FAILURE.** Only one run may be pending or
 * running at a time (a second doubles every Cliniko request against a per-key rate budget), and
 * the conflict body carries the ACTIVE run's `run_uuid` so the caller can watch that one instead.
 * Callers must handle it as a redirect and surface it as a message, never as an error toast.
 *
 * ⚠️ **No `timeout` override.** Both start paths return immediately; a long timeout here would
 * only mask an unreachable service.
 */
export const startImportRun = async (payload: { dry_run: boolean }): Promise<ClinikoImportRunStartResponse> => {
	const response = await http.post(
		`${API_BASE_URL}/practitioner/cliniko/import-runs`,
		{ dry_run: payload.dry_run }
	);
	return response.data;
};

/** Every run, newest first (`created_at` desc, `id` desc so the order is deterministic). */
export const listImportRuns = async (): Promise<ClinikoImportRunListResponse> => {
	const response = await http.get(`${API_BASE_URL}/practitioner/cliniko/import-runs`);
	return response.data;
};

/** One run — the poll target while its status is `pending` or `running`. */
export const getImportRun = async (runUuid: string): Promise<ClinikoImportRun> => {
	const response = await http.get(`${API_BASE_URL}/practitioner/cliniko/import-runs/${runUuid}`);
	return response.data;
};

/**
 * Re-dispatch THIS run. Sections that already succeeded are skipped; everything else re-enters
 * from the top, which is safe because every importer is an idempotent upsert on its Cliniko key.
 *
 * ⚠️ **A `running` row is resumable and that is deliberate** — a worker killed at the process
 * timeout or by a rolling deploy never writes a status, so `running` is exactly the shape a
 * killed run leaves behind. Only a `completed` run is refused, with a 409.
 */
export const resumeImportRun = async (runUuid: string): Promise<ClinikoImportRunStartResponse> => {
	const response = await http.post(
		`${API_BASE_URL}/practitioner/cliniko/import-runs/${runUuid}/resume`,
		{}
	);
	return response.data;
};
